import React, { useState, useRef } from 'react';
import { Form, Button, Card, Alert, ProgressBar, Badge, Modal } from 'react-bootstrap';
import { FaUpload, FaFileExcel, FaCheckCircle, FaExclamationTriangle, FaUser, FaCalendarAlt, FaTrashAlt, FaInfoCircle, FaSyncAlt } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { uploadAttendance, deleteAttendanceForMonth } from '../../services/firebase/attendanceService';

const AttendanceUpload = ({ month, year, onUploadSuccess, onUploadError }) => {
  const [file, setFile] = useState(null);
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatusText, setUploadStatusText] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const [result, setResult] = useState(null);
  const [autoReplace, setAutoReplace] = useState(true);

  // Duplicate / Overwrite handling
  const [showOverwriteModal, setShowOverwriteModal] = useState(false);
  const [duplicateInfo, setDuplicateInfo] = useState(null);

  // Manual Delete Modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const supportedFormats = ['.xlsx', '.xls', '.pdf'];
  const maxFileSize = 10 * 1024 * 1024; // 10MB

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleFileSelect = (selectedFile) => {
    const fileExt = '.' + selectedFile.name.split('.').pop().toLowerCase();
    if (!supportedFormats.includes(fileExt)) {
      toast.error(`Please upload an Excel or PDF file (.xlsx, .xls, .pdf)`);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    if (selectedFile.size > maxFileSize) {
      toast.error(`File size exceeds 10MB limit`);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    setFile(selectedFile);
    setResult(null);
    setDuplicateInfo(null);
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(e.target.files[0]);
    }
  };

  const handleUpload = async (overwrite = autoReplace) => {
    if (!file) {
      toast.error('Please select a file');
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setUploadStatusText('Reading attendance file...');
    setResult(null);
    setShowOverwriteModal(false);

    try {
      const targetM = month || (new Date().getMonth() + 1);
      const targetY = year || new Date().getFullYear();

      const onProgress = (percent, statusText) => {
        setUploadProgress(percent);
        if (statusText) setUploadStatusText(statusText);
      };

      const data = await uploadAttendance(file, targetM, targetY, overwrite, onProgress);

      setUploadProgress(100);
      setUploadStatusText('Completed successfully!');

      setResult(data);
      if (data.recordCount > 0) {
        if (data.overwritten && data.deletedCount > 0) {
          toast.success(`Previous sheet (${data.deletedCount} records) deleted. Uploaded ${data.recordCount} new records for ${getMonthName(data.month)} ${data.year}`);
        } else {
          toast.success(`Successfully uploaded ${data.recordCount} records for ${data.empName}`);
        }
      } else {
        toast.warning(`Uploaded 0 records. Please check if the Excel/PDF file has the correct headers.`);
      }

      if (onUploadSuccess) {
        onUploadSuccess(data);
      }

      setTimeout(() => {
        setFile(null);
        setUploadProgress(0);
        setUploadStatusText('');
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }, 1200);

    } catch (error) {
      console.error('Upload error:', error);

      // Handle duplicate records response
      if (error.code === 'DUPLICATE_RECORDS' || error.message === 'DUPLICATE_RECORDS' || error.response?.data?.code === 'DUPLICATE_RECORDS') {
        const info = error.data || error.response?.data?.data || {};
        setDuplicateInfo(info);
        setShowOverwriteModal(true);
        setUploading(false);
        setUploadProgress(0);
        setUploadStatusText('');
        return;
      }

      toast.error(error.response?.data?.message || error.message || 'Failed to upload attendance');

      if (onUploadError) {
        onUploadError(error);
      }
    } finally {
      setUploading(false);
    }
  };

  const handleOverwriteConfirm = () => {
    setShowOverwriteModal(false);
    handleUpload(true);
  };

  const handleManualDeleteMonth = async () => {
    setDeleting(true);
    try {
      const targetM = month || (new Date().getMonth() + 1);
      const targetY = year || new Date().getFullYear();
      const res = await deleteAttendanceForMonth(targetM, targetY);
      if (res.deletedCount > 0) {
        toast.success(`Successfully deleted ${res.deletedCount} attendance records for ${getMonthName(targetM)} ${targetY}`);
      } else {
        toast.info(`No attendance records found for ${getMonthName(targetM)} ${targetY}`);
      }
      setShowDeleteModal(false);
      setResult(null);
      if (onUploadSuccess) {
        onUploadSuccess({ month: targetM, year: targetY, recordCount: 0 });
      }
    } catch (err) {
      console.error('Delete error:', err);
      toast.error(err.message || 'Failed to delete attendance sheet');
    } finally {
      setDeleting(false);
    }
  };

  const getMonthName = (m) => {
    const months = ['January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'];
    return months[(parseInt(m, 10) || 1) - 1] || '';
  };

  const currentMonthName = getMonthName(month || (new Date().getMonth() + 1));
  const currentYearVal = year || new Date().getFullYear();

  return (
    <>
      <Card className="shadow-sm border-0 rounded-4">
        <Card.Header className="bg-white border-bottom py-3">
          <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center gap-2">
            <h5 className="mb-0 fw-bold text-dark fs-6 fs-sm-5">
              <FaUpload className="me-2 text-primary" />
              Upload Monthly Attendance Sheet
            </h5>
            <Badge bg="primary" className="px-3 py-2 rounded-pill">
              Target: {currentMonthName} {currentYearVal}
            </Badge>
          </div>
        </Card.Header>
        <Card.Body className="p-3 p-md-4">
          {/* Instructions */}
          <Alert variant="info" className="mb-3 mb-md-4 rounded-3 border-0 bg-info bg-opacity-10 text-dark small">
            <strong>Upload Format:</strong> Upload an Excel (.xlsx, .xls) or PDF biometric report for <strong>{currentMonthName} {currentYearVal}</strong>.
            <br />
            <span className="text-muted">
              The file can contain attendance for single or multiple employees (Emp Code, Name, Department, Date, InTime, OutTime, Status, etc.).
            </span>
          </Alert>

          {/* Auto-replace Setting Banner */}
          <div className="bg-light p-3 rounded-3 mb-3 mb-md-4 border d-flex flex-column flex-sm-row align-items-start align-items-sm-center justify-content-between gap-3">
            <div>
              <div className="fw-bold text-dark d-flex align-items-center">
                <FaInfoCircle className="text-primary me-2 flex-shrink-0" />
                Sheet Replacement Setting
              </div>
              <small className="text-muted">
                When multiple sheets are uploaded for <strong>{currentMonthName} {currentYearVal}</strong>, the previous attendance sheet will be automatically deleted so the new sheet cleanly takes its place.
              </small>
            </div>
            <Form.Check
              type="switch"
              id="auto-replace-switch"
              checked={autoReplace}
              onChange={(e) => setAutoReplace(e.target.checked)}
              label={<span className="fw-bold text-dark text-nowrap">{autoReplace ? "Auto-Replace Enabled" : "Confirm Before Overwrite"}</span>}
              className="flex-shrink-0"
            />
          </div>

          {/* Drag and Drop Zone */}
          <div
            className={`upload-zone ${dragActive ? 'drag-active' : ''} ${file ? 'has-file' : ''}`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={() => !uploading && fileInputRef.current?.click()}
            style={{
              border: dragActive ? '2px dashed #0d6efd' : '2px dashed #ced4da',
              borderRadius: '12px',
              textAlign: 'center',
              cursor: uploading ? 'not-allowed' : 'pointer',
              backgroundColor: dragActive ? '#e7f1ff' : file ? '#f8f9fa' : '#ffffff',
              transition: 'all 0.3s ease'
            }}
          >
            <input
              id="fileInput"
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,.pdf"
              onChange={handleFileChange}
              style={{ display: 'none' }}
              disabled={uploading}
            />

            <div className="mb-2 mb-md-3">
              {file ? (
                file.name.toLowerCase().endsWith('.pdf') 
                  ? <FaUpload size={40} color="#dc3545" /> 
                  : <FaFileExcel size={40} color="#217346" />
              ) : (
                <FaUpload size={40} color="#6c757d" />
              )}
            </div>

            {file ? (
              <>
                <h6 className="text-success fw-bold mb-1 fs-6">{file.name}</h6>
                <p className="text-muted mb-0 small">
                  {(file.size / 1024).toFixed(2)} KB • Tap to choose a different file
                </p>
              </>
            ) : (
              <>
                <h6 className="fw-bold text-dark fs-6">Tap to select or Drag & Drop Sheet</h6>
                <p className="text-muted mb-1 small">Supports Excel (.xlsx, .xls) and PDF reports (Max: 10MB)</p>
              </>
            )}
          </div>

          {/* Upload Progress */}
          {uploading && (
            <div className="mt-4 p-3 bg-light rounded-3 border">
              <ProgressBar
                now={uploadProgress}
                label={`${uploadProgress}%`}
                animated
                striped
                variant="primary"
                style={{ height: '20px', borderRadius: '10px' }}
              />
              <p className="text-center text-muted mt-2 mb-0 fw-semibold small">
                {uploadStatusText || (uploadProgress < 50 ? 'Reading file...' : uploadProgress < 85 ? 'Parsing & replacing previous records...' : 'Finalizing save...')}
              </p>
            </div>
          )}

          {/* Upload Result */}
          {result && !uploading && (
            <Alert variant={result.recordCount > 0 ? "success" : "warning"} className="mt-4 rounded-3 shadow-sm border-0">
              <div className="d-flex align-items-center mb-2">
                {result.recordCount > 0 ? (
                  <FaCheckCircle className="me-2 text-success" size={22} />
                ) : (
                  <FaExclamationTriangle className="me-2 text-warning" size={22} />
                )}
                <h6 className="mb-0 fw-bold">{result.recordCount > 0 ? "Attendance Sheet Uploaded Successfully!" : "No Valid Records Found"}</h6>
              </div>
              <div className="row mt-3 g-2 align-items-center">
                <div className="col-md-7">
                  {result.recordCount > 0 ? (
                    <>
                      <div className="d-flex align-items-center mb-1">
                        <FaUser className="me-2 text-secondary" />
                        <span className="fw-semibold">{result.empName} {result.empCode && result.empCode !== 'N/A' ? `(${result.empCode})` : ''}</span>
                      </div>
                      <small className="text-muted d-block">Department: {result.department || 'All / General'}</small>
                    </>
                  ) : (
                    <small className="text-muted">
                      Could not detect valid attendance rows. Please verify column headers (Emp Code, Date, InTime, OutTime, Status).
                    </small>
                  )}
                </div>
                <div className="col-md-5 text-md-end">
                  {result.recordCount > 0 && (
                    <div className="d-flex flex-column align-items-md-end gap-1">
                      <div className="d-flex align-items-center text-muted small mb-1">
                        <FaCalendarAlt className="me-1" />
                        <span>{getMonthName(result.month)} {result.year}</span>
                      </div>
                      <div className="d-flex gap-2 flex-wrap justify-content-md-end">
                        {result.overwritten && result.deletedCount > 0 && (
                          <Badge bg="warning" text="dark" className="px-2 py-1">
                            <FaTrashAlt className="me-1" /> Previous Sheet Deleted ({result.deletedCount} records replaced)
                          </Badge>
                        )}
                        <Badge bg="success" className="px-2 py-1">
                          {result.recordCount} records saved
                        </Badge>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </Alert>
          )}

          {/* Upload Button */}
          <div className="mt-4">
            <Button
              variant="primary"
              onClick={() => handleUpload(autoReplace)}
              disabled={!file || uploading}
              className="w-100 py-2 fw-bold shadow-sm rounded-3"
              size="lg"
            >
              {uploading ? (
                <>
                  <FaSyncAlt className="spinner-border spinner-border-sm me-2" />
                  Processing Sheet...
                </>
              ) : (
                <>
                  <FaUpload className="me-2" />
                  <span className="d-none d-sm-inline">
                    {autoReplace ? `Upload & Replace Sheet for ${currentMonthName} ${currentYearVal}` : `Upload Attendance Sheet`}
                  </span>
                  <span className="d-inline d-sm-none">
                    {autoReplace ? `Upload & Replace (${currentMonthName} ${currentYearVal})` : `Upload Sheet`}
                  </span>
                </>
              )}
            </Button>
          </div>

          {/* Reset / Clear Month Sheet Section for HR/Admin */}
          <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center gap-2 mt-4 pt-3 border-top">
            <small className="text-muted">
              Need to clear this month's attendance sheet without uploading?
            </small>
            <Button
              variant="outline-danger"
              size="sm"
              onClick={() => setShowDeleteModal(true)}
              disabled={uploading || deleting}
              className="fw-semibold rounded-pill px-3 w-100 w-sm-auto"
            >
              <FaTrashAlt className="me-1" /> Delete {currentMonthName} {currentYearVal} Sheet
            </Button>
          </div>
        </Card.Body>
      </Card>

      {/* Overwrite Confirmation Modal (Triggered when autoReplace is turned off and duplicates exist) */}
      <Modal show={showOverwriteModal} onHide={() => setShowOverwriteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="fw-bold">
            <FaExclamationTriangle className="text-warning me-2" />
            Previous Attendance Sheet Exists
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {duplicateInfo && (
            <>
              <p className="mb-2">
                An attendance sheet has already been uploaded for this month:
              </p>
              <div className="bg-light p-3 rounded-3 mb-3 border">
                <div><strong>Period:</strong> {getMonthName(duplicateInfo.month)} {duplicateInfo.year}</div>
                <div><strong>Previous Sheet Records:</strong> <span className="text-danger fw-bold">{duplicateInfo.existingCount}</span></div>
                <div><strong>New Sheet Records:</strong> <span className="text-success fw-bold">{duplicateInfo.newCount}</span></div>
                {duplicateInfo.empName && duplicateInfo.empName !== 'Employees' && (
                  <div><strong>Employee:</strong> {duplicateInfo.empName} ({duplicateInfo.empCode})</div>
                )}
              </div>
              <Alert variant="warning" className="mb-0 small">
                Uploading this new sheet will <strong>completely delete the previous attendance sheet</strong> ({duplicateInfo.existingCount} records) for {getMonthName(duplicateInfo.month)} {duplicateInfo.year} and replace it with the new data.
              </Alert>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowOverwriteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" className="fw-bold" onClick={handleOverwriteConfirm}>
            <FaTrashAlt className="me-1" /> Delete Previous & Upload New Sheet
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Manual Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => !deleting && setShowDeleteModal(false)} centered>
        <Modal.Header closeButton={!deleting}>
          <Modal.Title className="text-danger fw-bold">
            <FaTrashAlt className="me-2" />
            Delete Monthly Attendance Sheet
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Are you sure you want to delete the attendance sheet for <strong>{currentMonthName} {currentYearVal}</strong>?
          </p>
          <Alert variant="danger" className="small mb-0">
            <strong>Warning:</strong> All attendance records uploaded for {currentMonthName} {currentYearVal} will be permanently deleted from the database.
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button variant="danger" className="fw-bold" onClick={handleManualDeleteMonth} disabled={deleting}>
            {deleting ? 'Deleting...' : 'Yes, Delete Sheet'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default AttendanceUpload;
